import React, { useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Building2,
  Clock,
  MessageSquarePlus,
  ClipboardList,
  ListTodo,
  GripVertical,
  MapPin,
  Plus,
  DollarSign,
  User,
  Tag
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { useToastNotification } from '@/contexts/ToastContext';

// Pipeline stages configuration
const PIPELINE_STAGES = [
  { id: 'cold', label: 'Cold', description: 'New contacts not yet engaged' },
  { id: 'warm', label: 'Warm', description: 'Initial positive interest shown' },
  { id: 'engaged', label: 'Engaged', description: 'Active discussions ongoing' },
  { id: 'verbal', label: 'Verbal', description: 'Verbal commitment received' },
  { id: 'paused', label: 'Paused', description: 'Temporarily on hold' },
];

// Sample contact data
const initialContacts = [
  {
    id: '1',
    name: 'Sarah Chen',
    organization: 'Tech Ventures Capital',
    estimatedAmount: '$5M',
    region: 'APAC',
    lastTouch: '2d ago',
    owner: 'OL',
    stage: 'warm',
    tags: ['Series A']
  },
  {
    id: '2',
    name: 'Michael Roberts',
    organization: 'Blue Ocean Fund',
    estimatedAmount: '$10M',
    region: 'NA',
    lastTouch: '5d ago',
    owner: 'JK',
    stage: 'engaged',
    tags: ['Growth']
  },
  {
    id: '3',
    name: 'Emily Watson',
    organization: 'Greenfield Ventures',
    estimatedAmount: '$2.5M',
    region: 'EMEA',
    lastTouch: '1d ago',
    owner: 'OL',
    stage: 'cold',
    tags: ['Seed']
  },
  {
    id: '4',
    name: 'David Kim',
    organization: 'Horizon Capital',
    estimatedAmount: '$25M',
    region: 'NA',
    lastTouch: '3d ago',
    owner: 'MR',
    stage: 'verbal',
    tags: ['Late Stage']
  },
  {
    id: '5',
    name: 'Jennifer Martinez',
    organization: 'Alpha Group',
    estimatedAmount: null,
    region: 'LATAM',
    lastTouch: '7d ago',
    owner: 'OL',
    stage: 'paused',
    tags: ['SaaS']
  },
  {
    id: '6',
    name: 'James Thompson',
    organization: 'Summit Partners',
    estimatedAmount: '$8M',
    region: 'EMEA',
    lastTouch: '4d ago',
    owner: 'JK',
    stage: 'warm',
    tags: ['FinTech']
  },
  {
    id: '7',
    name: 'Lisa Anderson',
    organization: 'Redwood Ventures',
    estimatedAmount: '$15M',
    region: 'NA',
    lastTouch: '1d ago',
    owner: 'OL',
    stage: 'engaged',
    tags: ['AI/ML']
  },
  {
    id: '8',
    name: 'Robert Garcia',
    organization: 'Nexus Ventures',
    estimatedAmount: '$3M',
    region: 'NA',
    lastTouch: '6d ago',
    owner: 'MR',
    stage: 'cold',
    tags: ['Consumer']
  },
  {
    id: '9',
    name: 'Amanda Lee',
    organization: 'Innovate Fund',
    estimatedAmount: '$7M',
    region: 'APAC',
    lastTouch: '2d ago',
    owner: 'JK',
    stage: 'warm',
    tags: ['Enterprise']
  },
  {
    id: '10',
    name: 'Christopher Brown',
    organization: 'Vanguard Capital',
    estimatedAmount: null,
    region: 'NA',
    lastTouch: '10d ago',
    owner: 'OL',
    stage: 'cold',
    tags: ['PropTech']
  },
  {
    id: '11',
    name: 'Rachel Nguyen',
    organization: 'Pacific Ventures',
    estimatedAmount: '$12M',
    region: 'APAC',
    lastTouch: '3d ago',
    owner: 'MR',
    stage: 'engaged',
    tags: ['Hardware']
  },
  {
    id: '12',
    name: 'Daniel Wilson',
    organization: 'Capital Bridge',
    estimatedAmount: '$4M',
    region: 'NA',
    lastTouch: '8d ago',
    owner: 'JK',
    stage: 'paused',
    tags: ['GovTech']
  },
];

// Contact Card Component
const ContactCard = ({ contact, onDragStart, onDragEnd, isDragging }) => {
  const [showActions, setShowActions] = useState(false);
  const [isDraggingLocal, setIsDraggingLocal] = useState(false);

  const handleDragStart = (e) => {
    setIsDraggingLocal(true);
    // Set drag image
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', contact.id);
    // Call parent handler
    onDragStart(e, contact);
  };

  const handleDragEnd = (e) => {
    setIsDraggingLocal(false);
    onDragEnd(e);
  };

  return (
    <div
      draggable="true"
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
      className={`group relative bg-[#111318] border border-white/6 rounded-lg p-3 cursor-grab active:cursor-grabbing hover:border-white/12 transition-all duration-150 ${
        isDraggingLocal ? 'opacity-50 scale-95' : 'opacity-100'
      }`}
    >
      {/* Drag Handle */}
      <div className="absolute left-1 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-40 transition-opacity pointer-events-none">
        <GripVertical size={14} className="text-zinc-500" />
      </div>

      {/* Card Content */}
      <div className="pl-3">
        {/* Header: Name + Owner */}
        <div className="flex items-start justify-between gap-2 mb-1.5">
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-medium text-zinc-100 truncate">
              {contact.name}
            </h4>
            <p className="text-xs text-zinc-500 truncate flex items-center gap-1">
              <Building2 size={10} className="flex-shrink-0" />
              {contact.organization}
            </p>
          </div>
          <div className="w-6 h-6 rounded-full bg-[#1a1d24] border border-white/10 flex items-center justify-center text-[10px] font-medium text-zinc-400 flex-shrink-0">
            {contact.owner}
          </div>
        </div>

        {/* Middle Row: Amount + Region */}
        <div className="flex items-center gap-3 mb-2">
          {contact.estimatedAmount && (
            <span className="text-xs font-medium text-emerald-400/90">
              {contact.estimatedAmount}
            </span>
          )}
          <span className="text-[10px] text-zinc-500 flex items-center gap-0.5">
            <MapPin size={9} />
            {contact.region}
          </span>
        </div>

        {/* Footer: Last Touch + Tag */}
        <div className="flex items-center justify-between">
          <span className="text-[10px] text-zinc-600 flex items-center gap-1">
            <Clock size={10} />
            {contact.lastTouch}
          </span>
          {contact.tags[0] && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-800/50 text-zinc-500 border border-white/5">
              {contact.tags[0]}
            </span>
          )}
        </div>
      </div>

      {/* Quick Actions Overlay */}
      <AnimatePresence>
        {showActions && !isDraggingLocal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.1 }}
            className="absolute inset-0 bg-[#111318]/95 rounded-lg flex items-center justify-center gap-2 backdrop-blur-sm"
          >
            <button
              className="p-2 rounded-md bg-white/5 hover:bg-white/10 text-zinc-400 hover:text-zinc-200 transition-colors"
              title="Add Note"
              onClick={(e) => { e.stopPropagation(); }}
            >
              <MessageSquarePlus size={14} />
            </button>
            <button
              className="p-2 rounded-md bg-white/5 hover:bg-white/10 text-zinc-400 hover:text-zinc-200 transition-colors"
              title="Log Interaction"
              onClick={(e) => { e.stopPropagation(); }}
            >
              <ClipboardList size={14} />
            </button>
            <button
              className="p-2 rounded-md bg-white/5 hover:bg-white/10 text-zinc-400 hover:text-zinc-200 transition-colors"
              title="Create Task"
              onClick={(e) => { e.stopPropagation(); }}
            >
              <ListTodo size={14} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Pipeline Column Component
const PipelineColumn = ({ stage, contacts, onDrop, onDragOver, onDragStart, onDragEnd, dragOverColumn }) => {
  const isDropTarget = dragOverColumn === stage.id;
  const count = contacts.length;

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onDragOver(e, stage.id);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onDrop(e, stage.id);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <div
      className={`flex flex-col flex-1 min-w-[200px] transition-all duration-150 ${
        isDropTarget ? 'bg-blue-500/10 ring-2 ring-blue-500/30 ring-inset' : ''
      }`}
      style={{ height: '100%' }}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onDragEnter={handleDragEnter}
    >
      {/* Column Header */}
      <div className="flex-shrink-0 px-3 py-3 bg-[#0a0a0a] border-b border-white/10">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-zinc-100 uppercase tracking-wide">{stage.label}</h3>
          <span className="text-xs text-zinc-400 bg-zinc-800 px-2.5 py-1 rounded-full font-medium">
            {count}
          </span>
        </div>
      </div>

      {/* Column Content - Single unified area for cards and drop zone */}
      <div 
        className="flex-1 p-2 space-y-2 overflow-y-auto"
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onDragEnter={handleDragEnter}
      >
        {contacts.map((contact) => (
          <ContactCard
            key={contact.id}
            contact={contact}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
          />
        ))}
        
        {/* Empty state message when no cards */}
        {contacts.length === 0 && (
          <div className="flex items-center justify-center h-32">
            <p className="text-xs text-zinc-600 leading-relaxed text-center px-3">
              {stage.description}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// Create Contact Modal Component
const CreateContactModal = ({ open, onOpenChange, onCreateContact, stages }) => {
  const [formData, setFormData] = useState({
    name: '',
    organization: '',
    estimatedAmount: '',
    region: 'NA',
    owner: 'OL',
    stage: 'cold',
    tag: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.organization.trim()) {
      return;
    }

    const newContact = {
      id: Date.now().toString(),
      name: formData.name.trim(),
      organization: formData.organization.trim(),
      estimatedAmount: formData.estimatedAmount ? `$${formData.estimatedAmount}` : null,
      region: formData.region,
      lastTouch: 'Just now',
      owner: formData.owner,
      stage: formData.stage,
      tags: formData.tag ? [formData.tag] : []
    };

    onCreateContact(newContact);
    
    // Reset form
    setFormData({
      name: '',
      organization: '',
      estimatedAmount: '',
      region: 'NA',
      owner: 'OL',
      stage: 'cold',
      tag: ''
    });
    onOpenChange(false);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add New Contact</DialogTitle>
          <DialogDescription>
            Create a new contact and place them in a pipeline stage.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="name" className="text-zinc-300 text-xs">
              Contact Name <span className="text-red-400">*</span>
            </Label>
            <div className="relative">
              <User size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="e.g., John Smith"
                className="pl-9 bg-[#111318] border-white/10 text-zinc-100 placeholder:text-zinc-600 focus:border-blue-500/50 focus:ring-blue-500/20"
                required
              />
            </div>
          </div>

          {/* Organization */}
          <div className="space-y-2">
            <Label htmlFor="organization" className="text-zinc-300 text-xs">
              Organization <span className="text-red-400">*</span>
            </Label>
            <div className="relative">
              <Building2 size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <Input
                id="organization"
                value={formData.organization}
                onChange={(e) => handleChange('organization', e.target.value)}
                placeholder="e.g., Acme Capital Partners"
                className="pl-9 bg-[#111318] border-white/10 text-zinc-100 placeholder:text-zinc-600 focus:border-blue-500/50 focus:ring-blue-500/20"
                required
              />
            </div>
          </div>

          {/* Estimated Amount & Region - Side by side */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="amount" className="text-zinc-300 text-xs">
                Estimated Amount
              </Label>
              <div className="relative">
                <DollarSign size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <Input
                  id="amount"
                  value={formData.estimatedAmount}
                  onChange={(e) => handleChange('estimatedAmount', e.target.value.replace(/[^0-9.MBKmbk]/g, ''))}
                  placeholder="e.g., 10M"
                  className="pl-9 bg-[#111318] border-white/10 text-zinc-100 placeholder:text-zinc-600 focus:border-blue-500/50 focus:ring-blue-500/20"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-zinc-300 text-xs">Region</Label>
              <Select value={formData.region} onValueChange={(val) => handleChange('region', val)}>
                <SelectTrigger className="bg-[#111318] border-white/10 text-zinc-300 text-sm focus:border-blue-500/50 focus:ring-blue-500/20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0f1115] border-white/10">
                  <SelectItem value="NA" className="text-sm">NA (North America)</SelectItem>
                  <SelectItem value="EMEA" className="text-sm">EMEA</SelectItem>
                  <SelectItem value="APAC" className="text-sm">APAC</SelectItem>
                  <SelectItem value="LATAM" className="text-sm">LATAM</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Owner & Tag - Side by side */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-zinc-300 text-xs">Owner</Label>
              <Select value={formData.owner} onValueChange={(val) => handleChange('owner', val)}>
                <SelectTrigger className="bg-[#111318] border-white/10 text-zinc-300 text-sm focus:border-blue-500/50 focus:ring-blue-500/20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0f1115] border-white/10">
                  <SelectItem value="OL" className="text-sm">OL</SelectItem>
                  <SelectItem value="JK" className="text-sm">JK</SelectItem>
                  <SelectItem value="MR" className="text-sm">MR</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="tag" className="text-zinc-300 text-xs">Tag</Label>
              <div className="relative">
                <Tag size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <Input
                  id="tag"
                  value={formData.tag}
                  onChange={(e) => handleChange('tag', e.target.value)}
                  placeholder="e.g., Series A"
                  className="pl-9 bg-[#111318] border-white/10 text-zinc-100 placeholder:text-zinc-600 focus:border-blue-500/50 focus:ring-blue-500/20"
                />
              </div>
            </div>
          </div>

          {/* Stage Selection */}
          <div className="space-y-2">
            <Label className="text-zinc-300 text-xs">Pipeline Stage</Label>
            <Select value={formData.stage} onValueChange={(val) => handleChange('stage', val)}>
              <SelectTrigger className="bg-[#111318] border-white/10 text-zinc-300 text-sm focus:border-blue-500/50 focus:ring-blue-500/20">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0f1115] border-white/10">
                {stages.map(stage => (
                  <SelectItem key={stage.id} value={stage.id} className="text-sm">
                    {stage.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <DialogFooter className="mt-6 gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              className="text-zinc-400 hover:text-zinc-200 hover:bg-white/5"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Plus size={16} />
              Add Contact
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Main Soft Circle Pipeline Component
const SoftCircle = () => {
  const toast = useToastNotification();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ownerFilter, setOwnerFilter] = useState('all');
  const [fundFilter, setFundFilter] = useState('all');
  const [regionFilter, setRegionFilter] = useState('all');
  const [draggedContact, setDraggedContact] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Fetch contacts from API on mount
  useEffect(() => {
    const fetchContacts = async () => {
      try {
        const response = await fetch(`${API_URL}/api/pipeline/soft-circle`, {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setContacts(data);
        }
      } catch (error) {
        console.error('Error fetching contacts:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchContacts();
  }, [API_URL]);

  // Get unique owners and regions for filters
  const owners = [...new Set(contacts.map(c => c.owner))];
  const regions = [...new Set(contacts.map(c => c.region))];

  // Filter contacts
  const filteredContacts = contacts.filter(contact => {
    if (ownerFilter !== 'all' && contact.owner !== ownerFilter) return false;
    if (regionFilter !== 'all' && contact.region !== regionFilter) return false;
    return true;
  });

  // Group contacts by stage
  const contactsByStage = PIPELINE_STAGES.reduce((acc, stage) => {
    acc[stage.id] = filteredContacts.filter(c => c.stage === stage.id);
    return acc;
  }, {});

  // Drag handlers
  const handleDragStart = useCallback((e, contact) => {
    setDraggedContact(contact);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', contact.id);
  }, []);

  const handleDragEnd = useCallback(() => {
    setDraggedContact(null);
    setDragOverColumn(null);
  }, []);

  const handleDragOver = useCallback((e, stageId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverColumn(stageId);
  }, []);

  const handleDrop = useCallback(async (e, targetStage) => {
    e.preventDefault();
    const contactId = e.dataTransfer.getData('text/plain');
    
    if (contactId) {
      // Optimistic update
      setContacts(prev => prev.map(contact => 
        contact.id === contactId 
          ? { ...contact, stage: targetStage }
          : contact
      ));
      
      // Update in backend
      try {
        await fetch(`${API_URL}/api/pipeline/soft-circle/${contactId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ stage: targetStage })
        });
        toast.success('Contact moved');
      } catch (error) {
        console.error('Error updating contact stage:', error);
        toast.error('Failed to move contact');
      }
    }
    
    setDraggedContact(null);
    setDragOverColumn(null);
  }, [API_URL, toast]);

  // Handle creating a new contact
  const handleCreateContact = useCallback(async (newContact) => {
    try {
      const response = await fetch(`${API_URL}/api/pipeline/soft-circle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(newContact)
      });
      
      if (response.ok) {
        const savedContact = await response.json();
        setContacts(prev => [savedContact, ...prev]);
        toast.success('Contact added');
      }
    } catch (error) {
      console.error('Error creating contact:', error);
      toast.error('Failed to add contact');
    }
  }, [API_URL, toast]);

  // Show loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-80px)]">
        <div className="text-zinc-500">Loading contacts...</div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
      className="flex flex-col h-[calc(100vh-48px)] lg:h-[calc(100vh-80px)] -mx-6 lg:-mx-10 -mb-6 lg:-mb-10"
    >
      {/* Page Header */}
      <div className="flex-shrink-0 bg-[#0a0a0a] border-b border-white/6 px-6 lg:px-10 py-4">
        {/* Title Row */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
          <div>
            <h1 className="text-2xl font-heading font-semibold text-zinc-100">Soft Circle</h1>
            <p className="text-sm text-zinc-500 mt-1">
              Track early-stage relationships with minimal friction
            </p>
          </div>
          
          {/* Add Contact Button */}
          <Button
            onClick={() => setIsCreateModalOpen(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white text-xs h-8 px-3"
          >
            <Plus size={14} />
            Add Contact
          </Button>
        </div>

        {/* Filters Row */}
        <div className="flex flex-wrap items-center gap-3">
          {/* Owner Filter */}
          <Select value={ownerFilter} onValueChange={setOwnerFilter}>
            <SelectTrigger className="w-[130px] h-8 bg-[#0f1115] border-white/10 text-zinc-300 text-xs">
              <SelectValue placeholder="Owner" />
            </SelectTrigger>
            <SelectContent className="bg-[#0f1115] border-white/10">
              <SelectItem value="all" className="text-xs">All Owners</SelectItem>
              {owners.map(owner => (
                <SelectItem key={owner} value={owner} className="text-xs">{owner}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Fund Filter */}
          <Select value={fundFilter} onValueChange={setFundFilter}>
            <SelectTrigger className="w-[130px] h-8 bg-[#0f1115] border-white/10 text-zinc-300 text-xs">
              <SelectValue placeholder="Fund" />
            </SelectTrigger>
            <SelectContent className="bg-[#0f1115] border-white/10">
              <SelectItem value="all" className="text-xs">All Funds</SelectItem>
              <SelectItem value="fund-1" className="text-xs">Fund I</SelectItem>
              <SelectItem value="fund-2" className="text-xs">Fund II</SelectItem>
              <SelectItem value="fund-3" className="text-xs">Fund III</SelectItem>
            </SelectContent>
          </Select>

          {/* Region Filter */}
          <Select value={regionFilter} onValueChange={setRegionFilter}>
            <SelectTrigger className="w-[130px] h-8 bg-[#0f1115] border-white/10 text-zinc-300 text-xs">
              <SelectValue placeholder="Region" />
            </SelectTrigger>
            <SelectContent className="bg-[#0f1115] border-white/10">
              <SelectItem value="all" className="text-xs">All Regions</SelectItem>
              {regions.map(region => (
                <SelectItem key={region} value={region} className="text-xs">{region}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Kanban Board - Full remaining height and full width */}
      <div className="flex-1 overflow-x-auto overflow-y-hidden" style={{ minHeight: 0 }}>
        <div className="h-full w-full flex divide-x divide-white/6">
          {PIPELINE_STAGES.map(stage => (
            <PipelineColumn
              key={stage.id}
              stage={stage}
              contacts={contactsByStage[stage.id] || []}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              dragOverColumn={dragOverColumn}
            />
          ))}
        </div>
      </div>

      {/* Create Contact Modal */}
      <CreateContactModal
        open={isCreateModalOpen}
        onOpenChange={setIsCreateModalOpen}
        onCreateContact={handleCreateContact}
        stages={PIPELINE_STAGES}
      />
    </motion.div>
  );
};

export default SoftCircle;
